<!--
  Import from a PDF. Four steps, one rule.

  §16.2 rule 1 is that the extraction is a DRAFT a person reviews and publishes. There is no Publish
  button on this screen at any step, and the completion step's heading says what has NOT happened
  before it says what has. A screen that read "your form is ready" is how an unread questionnaire
  reaches a patient.
-->
<h2 mat-dialog-title class="cfi__title">
  <mat-icon>picture_as_pdf</mat-icon>
  Import questions from a PDF
</h2>

<mat-dialog-content class="cfi">

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       1. CHOOSE
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="phase === 'choose'">

    <p class="cfi__lede">
      Upload &ldquo;{{ data.form.name }}&rdquo; as a PDF and every page is read by AI into a set of
      questions you can edit.
    </p>

    <!-- Said BEFORE the upload, not after it. -->
    <div class="cfi__alert cfi__alert--draft" data-test="draft-warning">
      <mat-icon>rate_review</mat-icon>
      <div>
        <strong>What comes back is an unreviewed draft, not a finished form.</strong>
        <p>
          The AI reads each page and guesses at the questions, the answer options and which are
          required. It drops pages, it mis-reads tick boxes, and it invents options that are not on
          your document. <strong>Nothing is published and nothing is sent to a patient</strong> —
          you open the draft in the editor, check every question against the original, and publish
          it yourself when it is right.
        </p>
      </div>
    </div>

    <p class="cfi__caps">
      PDF only, up to <strong>{{ maxPages }} pages</strong> and {{ maxFileMb }} MB. Each page costs
      a separate reading, which is why the limit is there.
    </p>

    <input type="file" class="cfi__file-input" accept="application/pdf,.pdf"
           #fileInput (change)="onFileChosen($event)" data-test="file-input">

    <div class="cfi__picker">
      <button mat-stroked-button type="button" (click)="fileInput.click()">
        <mat-icon>upload_file</mat-icon> Choose a PDF
      </button>
      <span class="cfi__chosen" *ngIf="file" data-test="chosen-file">
        <mat-icon>description</mat-icon>
        {{ file.name }}
        <span class="cfi__dim">
          ({{ megabytes(file.size) }} MB<span *ngIf="filePageCount !== null">,
            {{ filePageCount }} page(s)</span>)
        </span>
        <button mat-icon-button type="button" aria-label="Choose a different file"
                (click)="clearFile()">
          <mat-icon>close</mat-icon>
        </button>
      </span>
    </div>

    <!-- A refusal this client made on its own, named and kept on screen. -->
    <div class="cfi__alert cfi__alert--error" *ngIf="fileError" data-test="file-error">
      <mat-icon>error_outline</mat-icon>
      <div>{{ fileError }}</div>
    </div>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       2. UPLOADING
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="phase === 'uploading'">
    <p class="cfi__lede">Uploading &ldquo;{{ file?.name }}&rdquo;&hellip;</p>
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       3. WAITING — the part §16.1 exists for
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="phase === 'waiting'">
    <p class="cfi__lede" data-test="waiting-heading">
      Reading &ldquo;{{ currentImport?.sourceFileName || file?.name }}&rdquo;&hellip;
    </p>

    <mat-progress-bar mode="indeterminate" data-test="waiting-progress"></mat-progress-bar>

    <p class="cfi__dim">
      Each page is read on its own, so this takes about a minute for a short questionnaire and
      longer for a long one.
      <span *ngIf="currentImport?.pageCount">
        {{ currentImport?.pageCount }} page(s) to read.
      </span>
      <span *ngIf="elapsedSeconds > 0">{{ elapsedSeconds }}s so far.</span>
    </p>

    <!-- §16.1: the result survives the tab closing, so say so rather than trapping them here. -->
    <div class="cfi__alert cfi__alert--info" data-test="safe-to-leave">
      <mat-icon>schedule</mat-icon>
      <div>
        <strong>You can close this window - the reading carries on without you.</strong>
        <p>
          It runs on our servers, not in this tab, so you can close the dialog or even the browser.
          When it is done the draft waits for you under <em>Import history</em> on this form, and
          you can open it in the editor then.
        </p>
      </div>
    </div>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       4a. COMPLETED — what has NOT happened, first
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="phase === 'completed'">
    <div class="cfi__alert cfi__alert--draft" data-test="completion-heading">
      <mat-icon>rate_review</mat-icon>
      <div>
        <strong>The document has been read. Nothing has been published.</strong>
        <p>
          {{ extractedQuestionCount }} question(s) in {{ extractedSectionCount }} section(s) were
          read out of
          &ldquo;{{ currentImport?.sourceFileName }}&rdquo;<span *ngIf="currentImport?.pageCount">
            ({{ currentImport?.pageCount }} page(s))</span>.
          <strong>No person has checked any of them.</strong> Open the draft in the editor, read
          every question against the original document, and publish it yourself when it is right
          - until you do, this form is not sent to anybody.
        </p>
      </div>
    </div>

    <!--
      §16.2 rule 2's own outcome: read, refused by `validateSchema`, and KEPT. Said plainly — this
      is not a form yet — but still reviewable, because the pages have already been paid for.
    -->
    <div class="cfi__alert cfi__alert--error" *ngIf="needsRepair" data-test="needs-repair">
      <mat-icon>build</mat-icon>
      <div>
        <strong>This draft cannot be published as it stands - it needs repairing first.</strong>
        <p *ngIf="schemaErrors.length === 0">{{ importError }}</p>
        <p>
          Nothing has been lost: the questions are in the draft, and the editor shows what is wrong
          against the question it belongs to.
        </p>
      </div>
    </div>

    <!-- §16.2 rule 2 — stored WITH the draft so it can be repaired instead of re-extracted. -->
    <div class="cfi__alert cfi__alert--error" *ngIf="schemaErrors.length > 0"
         data-test="schema-errors">
      <mat-icon>rule</mat-icon>
      <div>
        <strong>
          {{ schemaErrors.length }} problem(s) in the extracted draft need fixing before it can be
          published.
        </strong>
        <ul class="cfi__list">
          <li *ngFor="let error of schemaErrors">{{ error }}</li>
        </ul>
        <p>They are shown against the questions they belong to in the editor.</p>
      </div>
    </div>

    <p class="cfi__dim" *ngIf="currentImport?.sourceFileUrl">
      <a [href]="currentImport?.sourceFileUrl" target="_blank" rel="noopener"
         data-test="source-link">
        <mat-icon>open_in_new</mat-icon> Open the original PDF
      </a>
      - keep it beside the editor while you check the questions.
    </p>
  </ng-container>

  <!-- ══════════════════════════════════════════════════════════════════════════════════════════
       4b. FAILED — the server's own reason
       ══════════════════════════════════════════════════════════════════════════════════════ -->
  <ng-container *ngIf="phase === 'failed'">
    <div class="cfi__alert cfi__alert--error">
      <mat-icon>report_problem</mat-icon>
      <div>
        <strong>&ldquo;{{ currentImport?.sourceFileName }}&rdquo; could not be read.</strong>
        <p data-test="failure-reason">{{ importError }}</p>
        <p class="cfi__dim">
          Nothing was changed on this form. You can try a different file, or build the questions by
          hand in the editor.
        </p>
      </div>
    </div>
  </ng-container>
</mat-dialog-content>

<!--
  ACTIONS.

  On the completion step the primary action is REVIEW. There is no publish action here at all — the
  only place a version is ever created is the builder's own Publish button, pressed after somebody
  has read the questions.
-->
<mat-dialog-actions align="end">

  <ng-container *ngIf="phase === 'choose'">
    <button mat-button type="button" (click)="close()">Cancel</button>
    <button mat-flat-button color="primary" type="button" [disabled]="!file" (click)="upload()"
            data-test="upload-action">
      <mat-icon>auto_awesome</mat-icon> Read the document
    </button>
  </ng-container>

  <ng-container *ngIf="phase === 'uploading'">
    <button mat-button type="button" disabled>Uploading&hellip;</button>
  </ng-container>

  <ng-container *ngIf="phase === 'waiting'">
    <button mat-button type="button" (click)="close()" data-test="leave-action">
      Close - leave it running
    </button>
  </ng-container>

  <ng-container *ngIf="phase === 'completed'">
    <button mat-button type="button" (click)="close()">Not now</button>
    <button mat-flat-button color="primary" type="button" (click)="review()"
            data-test="review-action">
      <mat-icon>fact_check</mat-icon> Review the draft in the editor
    </button>
  </ng-container>

  <ng-container *ngIf="phase === 'failed'">
    <button mat-button type="button" (click)="close()">Close</button>
    <button mat-stroked-button type="button" (click)="startOver()" data-test="retry-action">
      <mat-icon>upload_file</mat-icon> Try another file
    </button>
  </ng-container>
</mat-dialog-actions>
